import { useCallback, useEffect, useState } from "react";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, Info, Loader2, RefreshCw,
  ExternalLink,
} from "lucide-react";
import { partnerApi, formatPartnerError } from "./partnerApi";
import "./PartnerPortal.css";

const STATUS_ICON = {
  ok: { icon: CheckCircle2, color: "#22C55E", label: "Operational" },
  operational: { icon: CheckCircle2, color: "#22C55E", label: "Operational" },
  idle: { icon: Info, color: "#94A3B8", label: "Idle" },
  not_configured: { icon: Info, color: "#94A3B8", label: "Not configured" },
  warning: { icon: AlertTriangle, color: "#F59E0B", label: "Warning" },
  degraded: { icon: AlertTriangle, color: "#F59E0B", label: "Degraded" },
  outage: { icon: XCircle, color: "#EF4444", label: "Outage" },
  incident: { icon: XCircle, color: "#EF4444", label: "Incident" },
  maintenance: { icon: Info, color: "#3B82F6", label: "Maintenance" },
  unknown: { icon: Info, color: "#94A3B8", label: "Unknown" },
};

function StatusPill({ status }) {
  const cfg = STATUS_ICON[status] || STATUS_ICON.unknown;
  const Icon = cfg.icon;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem",
                     padding: "0.15rem 0.55rem", borderRadius: "0.35rem",
                     background: `${cfg.color}22`, color: cfg.color,
                     fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.04em",
                     textTransform: "uppercase" }}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function PartnerHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/health");
      setData(data);
    } catch (e) { console.error(formatPartnerError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="pp-health">
      <h1 className="hw-pp__page-title">
        <Activity className="w-5 h-5"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Integration Health Center
      </h1>
      <p className="hw-pp__page-sub">
        One-stop view of your integration&apos;s health.
        Feeds from your API keys, OAuth clients, webhook subscriptions,
        the HarborWell platform, and the Trust Center.
      </p>

      <div style={{ marginBottom: "0.75rem" }}>
        <button className="hw-pp__btn" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <a href="/trust-center" target="_blank" rel="noreferrer" className="hw-pp__btn"
            style={{ marginLeft: "0.35rem" }} data-testid="pp-health-trust-link">
          <ExternalLink className="w-3.5 h-3.5" /> Public Trust Center
        </a>
      </div>

      {loading && !data && (
        <div style={{ color: "#5EEAD4" }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
        </div>
      )}

      {data && (
        <>
          <div className="hw-pp__panel" data-testid="pp-health-overall"
                style={{ borderColor: STATUS_ICON[data.overall_status]?.color || "#94A3B8",
                          borderWidth: "2px" }}>
            <h3>
              <span>Overall status</span>
              <StatusPill status={data.overall_status} />
            </h3>
            <div style={{ fontSize: "0.85rem", color: "#94A3B8" }}>
              Last successful sync:{" "}
              <span style={{ color: "#E2E8F0" }}>
                {data.last_successful_sync_at
                  ? data.last_successful_sync_at.replace("T", " ").slice(0, 19) + " UTC"
                  : "—"}
              </span>
            </div>
          </div>

          <div className="hw-pp__panel">
            <h3>Components</h3>
            <table className="hw-pp__table" data-testid="pp-health-components">
              <thead>
                <tr><th>Component</th><th>Status</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {(data.components || []).map((c) => (
                  <tr key={c.key} data-testid={`pp-health-comp-${c.key}`}>
                    <td><strong style={{ color: "#F1F5F9" }}>{c.label}</strong></td>
                    <td><StatusPill status={c.status} /></td>
                    <td style={{ color: "#94A3B8", fontSize: "0.83rem" }}>{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hw-pp__panel">
            <h3>Active incidents</h3>
            {(data.active_incidents || []).length === 0 ? (
              <div style={{ color: "#22C55E", fontSize: "0.85rem" }}>
                <CheckCircle2 className="w-3.5 h-3.5"
                                style={{ display: "inline", verticalAlign: "text-bottom",
                                          marginRight: "0.3rem" }} />
                No active incidents.
              </div>
            ) : (
              <table className="hw-pp__table" data-testid="pp-health-incidents">
                <thead><tr><th>Title</th><th>Impact</th><th>Status</th><th>Started</th></tr></thead>
                <tbody>
                  {data.active_incidents.map((i) => (
                    <tr key={i.id}>
                      <td>{i.title}</td>
                      <td>{i.impact}</td>
                      <td><StatusPill status={i.status} /></td>
                      <td style={{ fontSize: "0.72rem" }}>{(i.started_at || "").slice(0, 19)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="hw-pp__panel">
            <h3>Scheduled maintenance</h3>
            {(data.scheduled_maintenance || []).length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.85rem" }}>None scheduled.</div>
            ) : (
              <table className="hw-pp__table" data-testid="pp-health-maintenance">
                <thead><tr><th>Title</th><th>Window</th></tr></thead>
                <tbody>
                  {data.scheduled_maintenance.map((m) => (
                    <tr key={m.id}>
                      <td>{m.title}</td>
                      <td style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                        {m.window_start} → {m.window_end}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
